import React from 'react';
import { fetchData } from '../utils/runtime';

export function menProfileKey(profile) {
    return String(profile.__id ?? profile.EMAIL ?? profile.fullname1);
}

const emptyListing = () => ({profiles: [], total: null, facets: {ethnicities: [], countries: []},
    initialLoading: false, loadingMore: false, error: null, hasMore: false});

export function useMenListing({search = '', ethnicity = '', country = '', package: pkg = '',
    section = 'all', womanEmail = '', enabled = true, routeKey = ''} = {}) {
    const filters = JSON.stringify([ethnicity || '', country || '', pkg || '', section, womanEmail, enabled, routeKey]);
    const identity = JSON.stringify([search, filters]);
    const identityRef = React.useRef(identity);
    identityRef.current = identity;
    const [state, setState] = React.useState(emptyListing);
    const [revision, setRevision] = React.useState(0);
    const previous = React.useRef(null);
    const active = React.useRef(null);

    React.useEffect(() => {
        const prior = previous.current;
        previous.current = {identity, filters, revision};
        const session = {identity, alive: true, busy: false, offset: 0, failed: false, hasMore: false};
        active.current = session;
        setState({...emptyListing(), initialLoading: enabled});
        const valid = () => session.alive && identityRef.current === identity && active.current === session;
        session.request = async (offset) => {
            if (!valid() || session.busy || !enabled) return;
            session.busy = true;
            session.failed = false;
            session.controller = new AbortController();
            setState(old => ({...old, error: null, initialLoading: offset === 0, loadingMore: offset > 0}));
            const params = new URLSearchParams({limit: '20', offset: String(offset), search,
                ethnicity: ethnicity || '', country: country || '', package: pkg || '', section});
            if (womanEmail) params.set('woman_email', womanEmail);
            try {
                const data = await fetchData(`/api/sirri_api/get_men_normal?${params.toString()}`, {signal: session.controller.signal});
                if (!valid()) return;
                if (data.responsecode !== 200 || !Array.isArray(data.acc)) throw new Error(data.message || 'Could not load men.');
                const next = data.next_offset;
                session.hasMore = data.has_more === true && data.acc.length > 0 && Number.isInteger(next) && next > offset;
                session.offset = session.hasMore ? next : offset;
                setState(old => {
                    const profiles = offset === 0 ? [] : [...old.profiles];
                    const seen = new Set(profiles.map(menProfileKey));
                    data.acc.forEach(profile => {const key = menProfileKey(profile); if (!seen.has(key)) {seen.add(key); profiles.push(profile);}});
                    return {...old, profiles, total: data.total, facets: data.facets || old.facets, hasMore: session.hasMore, error: null};
                });
            } catch (error) {
                if (!valid()) return;
                session.failed = true;
                // Keep offset at the failed page so Retry never skips results.
                session.offset = offset;
                setState(old => ({...old, error: error.message || 'Could not load men.'}));
            } finally {
                if (valid()) {
                    session.busy = false;
                    setState(old => ({...old, initialLoading: false, loadingMore: false}));
                }
            }
        };
        const searchOnly = prior && prior.filters === filters && prior.revision === revision && prior.identity !== identity;
        const timer = enabled && searchOnly ? setTimeout(() => session.request(0), 300) : null;
        if (enabled && !searchOnly) session.request(0);
        return () => {session.alive = false; clearTimeout(timer); session.controller?.abort();};
    }, [identity, revision]);

    const loadMore = React.useCallback(() => {
        const session = active.current;
        if (session?.identity === identityRef.current && session.hasMore && !session.failed) session.request(session.offset);
    }, []);
    const retry = React.useCallback(() => {
        const session = active.current;
        if (session?.identity === identityRef.current && session.failed) session.request(session.offset);
    }, []);
    const refresh = React.useCallback(() => {
        if (active.current) {active.current.alive = false; active.current.controller?.abort();}
        setRevision(value => value + 1);
    }, []);
    return {...state, loadMore, retry, refresh};
}

// Keep this sentinel mounted, including while loading; disable observation on failure/end.
export function useListingObserver(listing) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        if (!ref.current || !listing.hasMore || listing.initialLoading || listing.loadingMore || listing.error || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) listing.loadMore();
        });
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, [listing.hasMore, listing.initialLoading, listing.loadingMore, listing.error, listing.loadMore]);
    return ref;
}

export function useListingProfile(url) {
    const [state, setState] = React.useState({data: {}, loading: !!url, error: null, url});
    const [revision, setRevision] = React.useState(0);
    const current = React.useRef(url);
    current.current = url;
    React.useEffect(() => {
        let alive = true;
        const controller = new AbortController();
        setState({data: {}, loading: !!url, error: null, url});
        if (url) {
            (async () => {
                try {
                    const response = await fetchData(url, {signal: controller.signal});
                    if (!alive || current.current !== url) return;
                    if (response.responsecode !== 200 || !response.data || typeof response.data !== 'object') throw new Error(response.message || 'Could not load profile.');
                    setState({data: response.data, loading: false, error: null, url});
                } catch (error) {
                    if (alive && current.current === url) setState({data: {}, loading: false, error: error.message || 'Could not load profile.', url});
                }
            })();
        }
        return () => {alive = false; controller.abort();};
    }, [url, revision]);
    const retry = React.useCallback(() => setRevision(value => value + 1), []);
    return {...(state.url === url ? state : {data: {}, loading: !!url, error: null}), retry};
}
